/**
 * WHERE A CHAPTER HEADING SITS (Andre, 2026-10-29): one rule for the live
 * story (narration.tsx) and the casebook (casebook-atoms.tsx).
 *
 * A chapter is an in-game day. The server sends, with each turn, the coded
 * slug line of the day that turn ENDED on ("Monday, April 6, 8:40 a.m.": the
 * moment the first turn to end on that date ended at, its morning unless that
 * turn spent two half-days from an afternoon, from the engine's half-day clock,
 * never a beat or an event title). A turn is printed under the day it BEGAN
 * on, which is the day the turn before it ended on; turn 0, the opening,
 * begins on day one, its own. So a move typed Monday at 1:10 p.m. that ends on
 * Tuesday stays on Monday's page, and Tuesday's heading opens the next turn.
 *
 * A day is headed once, above the first turn begun on it. A null chapter (a
 * case missing from the registry, an older server) neither opens a day nor
 * closes one, so a thread with no chapters draws no heading at all.
 *
 * Pure: the chapters in, the headings out. It reads nothing else.
 */

/**
 * For each turn, the heading printed above it, or null. `ended[i]` is the
 * chapter turn i ended on, exactly as the server sent it.
 */
export function chapterHeadings(ended: readonly (string | null)[]): readonly (string | null)[] {
  const headings: (string | null)[] = [];
  let open: string | null = null;
  for (const [index, own] of ended.entries()) {
    const began = index === 0 ? own : (ended[index - 1] ?? null);
    if (began !== null && began !== open) {
      open = began;
      headings.push(began);
    } else {
      headings.push(null);
    }
  }
  return headings;
}

/**
 * The heading above the line being worked, or null: the next turn begins on
 * the day the last one ended on, and is headed only if that day is not the one
 * already open. When it lands it is placed by `chapterHeadings`, the same rule,
 * so it does not move.
 */
export function headingForNext(ended: readonly (string | null)[]): string | null {
  if (ended.length === 0) return null;
  return chapterHeadings([...ended, null]).at(-1) ?? null;
}
